// #671 status reconciliation, the pure half (the rune stores in state.svelte.ts assign what
// these return). A cleared (no emoji, no text) or expired status is ABSENT from a map.

export interface MemberStatus {
  emoji: string | null;
  text: string | null;
  expiresAt: number | null;
}

export function isActiveStatus(status: MemberStatus): boolean {
  return Boolean(status.emoji || status.text);
}

export function isLiveStatus(status: MemberStatus, now: number): boolean {
  return isActiveStatus(status) && (status.expiresAt === null || status.expiresAt > now);
}

// A FRESH map: a user absent from the snapshot is dropped (a merge would strand them).
export function reconcileStatusSnapshot(
  statuses: ReadonlyArray<{ userId: string } & MemberStatus>,
  now: number = Date.now(),
): Record<string, MemberStatus> {
  const next: Record<string, MemberStatus> = {};
  for (const s of statuses) {
    if (isLiveStatus(s, now)) {
      next[s.userId] = { emoji: s.emoji, text: s.text, expiresAt: s.expiresAt };
    }
  }
  return next;
}

export function applyStatusChange(
  current: Readonly<Record<string, MemberStatus>>,
  userId: string,
  status: MemberStatus,
  now: number = Date.now(),
): Record<string, MemberStatus> {
  const next = { ...current };
  if (isLiveStatus(status, now)) {
    next[userId] = status;
  } else {
    delete next[userId];
  }
  return next;
}

// The same object when nothing expired, so an assign without change stays cheap.
export function pruneExpiredStatuses(
  current: Readonly<Record<string, MemberStatus>>,
  now: number,
): Readonly<Record<string, MemberStatus>> {
  const expired = Object.keys(current).filter((id) => !isLiveStatus(current[id], now));
  if (expired.length === 0) return current;
  const next = { ...current };
  for (const id of expired) delete next[id];
  return next;
}

export function nextStatusExpiry(
  current: Readonly<Record<string, MemberStatus>>,
): number | undefined {
  let soonest: number | undefined;
  for (const s of Object.values(current)) {
    if (s.expiresAt !== null && (soonest === undefined || s.expiresAt < soonest)) {
      soonest = s.expiresAt;
    }
  }
  return soonest;
}

// Re-saving with the duration untouched keeps the instant the status already had.
export function savedStatusExpiry(
  durationTouched: boolean,
  currentExpiresAt: string | null,
  computed: string | null,
  now: number,
): string | null {
  if (durationTouched || !currentExpiresAt) return computed;
  return Date.parse(currentExpiresAt) > now ? currentExpiresAt : computed;
}

// The label for a kept expiry: "Clears at 12:59 AM" today, "Clears Dec 3, 12:59 AM" otherwise.
export function clearsAtLabel(
  currentExpiresAt: string | null,
  now: Date,
  locale?: string,
): string | undefined {
  if (!currentExpiresAt) return undefined;
  const at = new Date(currentExpiresAt);
  if (!(at.getTime() > now.getTime())) return undefined;
  const time = at.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  if (at.toDateString() === now.toDateString()) return `Clears at ${time}`;
  return `Clears ${at.toLocaleDateString(locale, { month: "short", day: "numeric" })}, ${time}`;
}

export function epochMsToIso(expiresAt: number | null): string | null {
  if (expiresAt === null) return null; // null: "Don't clear"
  return new Date(expiresAt).toISOString();
}

export function ownStatusFromSnapshot(
  statuses: ReadonlyArray<{ userId: string } & MemberStatus>,
  userId: string,
  now: number = Date.now(),
): MemberStatus | undefined {
  const own = statuses.find((s) => s.userId === userId);
  if (!own || !isLiveStatus(own, now)) return undefined;
  return { emoji: own.emoji, text: own.text, expiresAt: own.expiresAt };
}

export interface MemberStatusSink {
  seed(statuses: Array<{ userId: string } & MemberStatus>): void;
}
export interface OwnStatusSink {
  applyServer(status: MemberStatus | undefined): void;
}

// One snapshot feeds both stores; my row (or its absence) overwrites my cached status.
export function applyStatusSnapshot(
  statuses: Array<{ userId: string } & MemberStatus>,
  me: string | undefined,
  members: MemberStatusSink,
  own: OwnStatusSink,
): void {
  members.seed(statuses);
  if (me) own.applyServer(ownStatusFromSnapshot(statuses, me));
}

export interface OwnStatusDraft {
  emoji: string | null;
  text: string | null;
  expiresAt: string | null;
}
export interface OwnStatusStore extends OwnStatusDraft {
  setStatus(emoji: string | null, text: string | null, expiresAt: string | null): void;
}

// Optimistic; a refusal or a closed socket restores the previous status and rethrows.
export async function saveOwnStatus(
  store: OwnStatusStore,
  next: OwnStatusDraft,
  send: () => Promise<void>,
): Promise<void> {
  const previous = { emoji: store.emoji, text: store.text, expiresAt: store.expiresAt };
  store.setStatus(next.emoji, next.text, next.expiresAt);
  try {
    await send();
  } catch (err) {
    store.setStatus(previous.emoji, previous.text, previous.expiresAt);
    throw err;
  }
}
